import type {
  AircraftTypeId,
  CabinConfiguration,
  Registration,
} from '../../../domain/models';

/**
 * UI-layer types for the cabin editor. Nothing here touches the simulation
 * worker directly: the editor emits `CabinEditorCommand` values through a
 * callback and the host forwards them to the worker client.
 */

/** How a pending acquisition is financed (Step 1 vocabulary, SPEC 1 §3). */
export type AcquisitionMode = 'PURCHASE' | 'OPERATING_LEASE' | 'FINANCE_LEASE';

export const ACQUISITION_MODES: readonly AcquisitionMode[] = [
  'PURCHASE',
  'OPERATING_LEASE',
  'FINANCE_LEASE',
];

export const ACQUISITION_MODE_LABELS: Readonly<Record<AcquisitionMode, string>> = {
  PURCHASE: 'Direct purchase',
  OPERATING_LEASE: 'Operating lease',
  FINANCE_LEASE: 'Finance lease',
};

/** An aircraft already in the fleet whose cabin can be refitted. */
export interface FleetCabinTarget {
  readonly registration: Registration;
  readonly typeId: AircraftTypeId;
  /** Cabin currently installed. */
  readonly cabin: CabinConfiguration;
}

/** What the editor is currently configuring. */
export type EditorSourceKind = 'ACQUISITION' | 'FLEET';

export type CabinApplyTarget =
  | {
      readonly kind: 'ACQUISITION';
      readonly typeId: AircraftTypeId;
      readonly mode: AcquisitionMode;
    }
  | {
      readonly kind: 'FLEET';
      readonly registration: Registration;
    };

/**
 * Commands issued by the editor.
 *
 * `ACQUIRE_AIRCRAFT` mirrors api_contracts.md, except that `purchaseType`
 * uses the three-way acquisition vocabulary from the Step 1 models instead of
 * the contract's `'LEASE' | 'BUY'`. `REFIT_AIRCRAFT` is the fleet modification
 * command; the worker must handle it (see the Step 8 notes).
 */
export type CabinEditorCommand =
  | {
      readonly type: 'ACQUIRE_AIRCRAFT';
      readonly typeId: AircraftTypeId;
      readonly purchaseType: AcquisitionMode;
      readonly cabin: CabinConfiguration;
    }
  | {
      readonly type: 'REFIT_AIRCRAFT';
      readonly registration: Registration;
      readonly cabin: CabinConfiguration;
    };

export type QuickPresetId =
  | 'HIGH_DENSITY_LOW_COST'
  | 'STANDARD_TWO_CLASS'
  | 'ULTRA_LUXURY_LONG_HAUL';

export interface QuickPresetDefinition {
  readonly id: QuickPresetId;
  readonly label: string;
  readonly description: string;
}

/** A player-saved cabin layout, tied to the aircraft type it was built for. */
export interface SavedCabinPreset {
  readonly id: string;
  readonly name: string;
  readonly typeId: AircraftTypeId;
  readonly cabin: CabinConfiguration;
  readonly savedAtIso: string;
}
